import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from 'components/Layout/Spinner';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { SCOPES } from 'common/permissions';
import InvoiceConfigurationDrawer from 'components/InvoiceConfiguration/InvoiceConfigurationDrawer';
import { hasPermission } from 'components/authorization/PermissionUtils';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

export type InvoiceConfiguration = {
  id: number;
  idCorporateEntity: number;
  countryCode: string;
  prefix?: string;
  numberFormat: string;
  separator: string;
  startingNumber: number;
  active: boolean;
  additionalSettings?: string;
  isDeleted: boolean;
};

interface InvoiceConfigurationTableProps {
  corporateEntityId: number;
  countryCode: string;
  disabled?: boolean;
}

const InvoiceConfigurationTable: React.FC<InvoiceConfigurationTableProps> = ({
  corporateEntityId,
  countryCode,
  disabled = false,
}) => {
  const { invoiceApi }: Api = useApi();
  const toast = useToastMessageQueue();

  const [configurations, setConfigurations] = useState<InvoiceConfiguration[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [editingConfig, setEditingConfig] = useState<InvoiceConfiguration | null>(null);

  const [paginationModel, setPaginationModel] = usePaginationPreference('invoiceConfigurationTable', 5);

  const canEdit = hasPermission({ scopes: [SCOPES['corporateentity.edit']] });

  useEffect(() => {
    if (corporateEntityId) {
      fetchConfigurations();
    }
  }, [corporateEntityId]);

  const fetchConfigurations = async () => {
    setLoading(true);
    try {
      const response = await invoiceApi.apiVversionInvoiceConfigurationEntityEntityIdGet(corporateEntityId, '1');
      if (response.data?.data) {
        setConfigurations(response.data.data as InvoiceConfiguration[]);
      }
    } catch (error) {
      handleAPIError(error, toast, {});
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    if (disabled) return;
    setEditingConfig(null);
    setDrawerOpen(true);
  };

  const handleEdit = (config: InvoiceConfiguration) => {
    if (disabled) return;
    setEditingConfig(config);
    setDrawerOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (disabled) return;
    
    if (!window.confirm(intl.get('invoiceConfig.confirmDelete'))) {
      return;
    }

    try {
      await invoiceApi.apiVversionInvoiceConfigurationIdDelete(id, '1');
      toast.success({ body: intl.get('invoiceConfig.deleteSuccess') });
      fetchConfigurations();
    } catch (error) {
      handleAPIError(error, toast, {});
    }
  };

  const handleSave = async (data: Partial<InvoiceConfiguration>) => {
    try {
      if (editingConfig?.id) {
        await invoiceApi.apiVversionInvoiceConfigurationIdPut(editingConfig.id, '1', {
          prefix: data.prefix,
          numberFormat: data.numberFormat,
          separator: data.separator,
          startingNumber: data.startingNumber,
          active: data.active,
          additionalSettings: data.additionalSettings,
        });
        toast.success({ body: intl.get('invoiceConfig.updateSuccess') });
      } else {
        await invoiceApi.apiVversionInvoiceConfigurationPost('1', {
          idCorporateEntity: corporateEntityId,
          countryCode: countryCode,
          prefix: data.prefix,
          numberFormat: data.numberFormat,
          separator: data.separator,
          startingNumber: data.startingNumber,
          active: data.active,
          additionalSettings: data.additionalSettings,
        });
        toast.success({ body: intl.get('invoiceConfig.createSuccess') });
      }

      setDrawerOpen(false);
      fetchConfigurations();
    } catch (error) {
      handleAPIError(error, toast, {});
    }
  };

  const formatInvoiceNumber = (config: InvoiceConfiguration): string => {
    const prefix = config.prefix || '';
    const separator = config.separator || '';
    const format = config.numberFormat || 'D4';
    const digits = parseInt(format.replace('D', ''), 10) || 4;
    const number = '1'.padStart(digits, '0');
    
    return prefix ? `${prefix}${separator}${number}` : number;
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: intl.get('invoiceConfig.table.id'),
      width: 80,
    },
    {
      field: 'countryCode',
      headerName: intl.get('invoiceConfig.table.countryCode'),
      width: 100,
    },
    {
      field: 'prefix',
      headerName: intl.get('invoiceConfig.table.prefix'),
      width: 120,
      renderCell: (params) => params.value || intl.get('invoiceConfig.table.noPrefix'),
    },
    {
      field: 'numberFormat',
      headerName: intl.get('invoiceConfig.table.numberFormat'),
      width: 100,
    },
    {
      field: 'separator',
      headerName: intl.get('invoiceConfig.table.separator'),
      width: 100,
    },
    {
      field: 'startingNumber',
      headerName: intl.get('invoiceConfig.table.startingNumber'),
      width: 120,
    },
    {
      field: 'preview',
      headerName: intl.get('invoiceConfig.table.preview'),
      width: 150,
      renderCell: (params) => (
        <Box
          sx={{
            fontFamily: 'monospace',
            fontSize: '14px',
            fontWeight: 600,
            color: 'primary.main',
          }}
        >
          {formatInvoiceNumber(params.row)}
        </Box>
      ),
    },
    {
      field: 'active',
      headerName: intl.get('invoiceConfig.table.active'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={0}
          editScopes={[SCOPES['corporateentity.edit']]}
          deleteScopes={[SCOPES['corporateentity.edit']]}
          onEdit={() => handleEdit(params.row)}
          onDelete={() => handleDelete(params.row.id)}
          disabled={disabled}
        />
      ),
    },
  ];

  if (!corporateEntityId || !countryCode) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {intl.get('invoiceConfig.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {intl.get('invoiceConfig.saveEntityFirst')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          {intl.get('invoiceConfig.title')}
        </Typography>
        {canEdit && !disabled && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            size="small"
          >
            {intl.get('invoiceConfig.addConfiguration')}
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2}>
        {intl.get('invoiceConfig.newConfigHelp')}
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <Spinner />
        </Box>
      ) : (
        <DataGrid
          rows={configurations}
          columns={columns}
          autoHeight
          pageSizeOptions={[5, 10, 25]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
          }}
        />
      )}

      <InvoiceConfigurationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
        config={editingConfig}
        corporateEntityId={corporateEntityId}
        countryCode={countryCode}
      />
    </Box>
  );
};

export default InvoiceConfigurationTable;
